import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { GitCompare, ArrowRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

function regionColor(type) {
  const map = {
    mff_fac: 'text-red-400', lambda: 'text-yellow-400', timing: 'text-cyan-400',
    kickdown: 'text-purple-400', enrichment: 'text-emerald-400', adaptation: 'text-pink-400',
    cal: 'text-orange-300', ext: 'text-blue-300', code: 'text-red-300'
  };
  return map[type] || 'text-white';
}

function regionBg(type) {
  const map = {
    mff_fac: 'bg-red-500/5 border-red-500/20', lambda: 'bg-yellow-500/5 border-yellow-500/20',
    timing: 'bg-cyan-500/5 border-cyan-500/20', kickdown: 'bg-purple-500/5 border-purple-500/20',
    enrichment: 'bg-emerald-500/5 border-emerald-500/20', adaptation: 'bg-pink-500/5 border-pink-500/20',
    cal: 'bg-orange-500/5 border-orange-500/20', ext: 'bg-blue-500/5 border-blue-500/20'
  };
  return map[type] || 'bg-[#0A0A0A] border-[#27272A]';
}

export default function TuneCompare({ tunes }) {
  const [file1, setFile1] = useState('');
  const [file2, setFile2] = useState('');
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const stockFile = 'WBAPK5C58AA652287_MSV80.1_0049RK0MK80S_fullread2.bin';
  const allBinFiles = [
    { filename: stockFile, label: 'STOCK K80S (Original)' },
    ...tunes.filter(t => t.filename !== stockFile).map(t => ({ filename: t.filename, label: t.filename }))
  ];

  const runCompare = async () => {
    if (!file1 || !file2) return;
    if (file1 === file2) { setError('Select two different files'); return; }
    setLoading(true); setError(null); setComparison(null);
    try {
      const res = await fetch(`${API_URL}/api/bin/compare?file1=${encodeURIComponent(file1)}&file2=${encodeURIComponent(file2)}`);
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail || 'Compare failed'); }
      setComparison(await res.json());
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-6" data-testid="tune-compare-tab">
      <Card className="bg-[#111111] border-[#27272A] rounded-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-white font-['Outfit']">
            <GitCompare className="w-5 h-5 text-cyan-400" />Tune Comparison Tool
          </CardTitle>
          <CardDescription className="text-zinc-400">Select two BIN files to see exactly which maps changed</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-zinc-500 uppercase tracking-wider mb-2 block">Base File</label>
              <select value={file1} onChange={e => setFile1(e.target.value)} data-testid="compare-file1-select"
                className="w-full bg-[#050505] border border-[#27272A] text-white rounded-sm px-3 py-2.5 text-sm focus:border-[#FF5E1A] outline-none">
                <option value="">Select base file...</option>
                {allBinFiles.map(f => <option key={f.filename} value={f.filename}>{f.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-zinc-500 uppercase tracking-wider mb-2 block">Modified File</label>
              <select value={file2} onChange={e => setFile2(e.target.value)} data-testid="compare-file2-select"
                className="w-full bg-[#050505] border border-[#27272A] text-white rounded-sm px-3 py-2.5 text-sm focus:border-[#FF5E1A] outline-none">
                <option value="">Select tune file...</option>
                {allBinFiles.map(f => <option key={f.filename} value={f.filename}>{f.label}</option>)}
              </select>
            </div>
          </div>
          <Button onClick={runCompare} disabled={!file1 || !file2 || loading} data-testid="compare-run-btn"
            className="bg-cyan-600 hover:bg-cyan-700 text-white rounded-sm disabled:opacity-50">
            <GitCompare className="w-4 h-4 mr-2" />{loading ? 'Comparing...' : 'Compare Files'}
          </Button>
          {error && <p className="text-red-400 text-sm">{error}</p>}
        </CardContent>
      </Card>

      {comparison && (
        <>
          <Card className="bg-[#111111] border-[#27272A] rounded-sm">
            <CardHeader>
              <CardTitle className="text-white text-lg font-['Outfit']">Summary</CardTitle>
              <CardDescription className="text-zinc-400 font-['JetBrains_Mono'] text-xs">{comparison.file1} vs {comparison.file2}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                {[
                  { label: "Changes", value: comparison.total_changes, color: "text-cyan-400" },
                  { label: "Bytes Modified", value: comparison.total_bytes_changed, color: "text-[#FF5E1A]" },
                  { label: "File Size", value: `${(comparison.file_size / 1024 / 1024).toFixed(1)} MB`, color: "text-white" },
                  { label: "% Changed", value: `${((comparison.total_bytes_changed / comparison.file_size) * 100).toFixed(4)}%`, color: "text-emerald-400" },
                ].map(s => (
                  <div key={s.label} className="bg-[#050505] border border-[#27272A] rounded-sm p-3">
                    <p className="text-xs text-zinc-600 uppercase tracking-wider">{s.label}</p>
                    <p className={`text-2xl font-bold font-['JetBrains_Mono'] ${s.color}`} data-testid="compare-total-changes">{s.value}</p>
                  </div>
                ))}
              </div>
              <p className="text-xs text-zinc-500 uppercase tracking-wider mb-3">By Region</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {comparison.region_summary.map((r) => (
                  <div key={r.region} className="flex items-center justify-between bg-[#050505] border border-[#27272A] rounded-sm px-3 py-2">
                    <span className="text-sm text-white">{r.region}</span>
                    <span className="text-xs text-[#FF5E1A] font-['JetBrains_Mono']">{r.bytes_changed} bytes</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#111111] border-[#27272A] rounded-sm">
            <CardHeader><CardTitle className="text-white text-lg font-['Outfit']">Detailed Changes</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-[600px] overflow-y-auto pr-2">
                {comparison.changes.map((c) => (
                  <div key={c.address} className={`rounded-sm p-3 border ${regionBg(c.region_type)}`} data-testid={`compare-change-${c.address}`}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <code className="text-xs font-['JetBrains_Mono'] text-cyan-300 bg-[#050505] px-1.5 py-0.5 rounded-sm">{c.address}</code>
                          <span className={`text-xs font-medium ${regionColor(c.region_type)}`}>{c.region}</span>
                          <span className="text-xs text-zinc-600">{c.size}B</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs font-['JetBrains_Mono']">
                          <span className="text-red-400 bg-red-500/5 px-1.5 py-0.5 rounded-sm">{c.old_hex}</span>
                          <ArrowRight className="w-3 h-3 text-zinc-600 flex-shrink-0" />
                          <span className="text-emerald-400 bg-emerald-500/5 px-1.5 py-0.5 rounded-sm">{c.new_hex}</span>
                        </div>
                        {c.old_decoded && (
                          <div className="flex items-center gap-2 text-xs mt-1">
                            <span className="text-red-300">{c.old_decoded}</span>
                            <ArrowRight className="w-3 h-3 text-zinc-600 flex-shrink-0" />
                            <span className="text-emerald-300">{c.new_decoded}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {comparison.changes.length >= 200 && <p className="text-xs text-zinc-600 mt-3 text-center">Showing first 200 changes</p>}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
